import * as React from 'react';
import Helmet from 'react-helmet';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import config from '@configs/config.json';
import { app as routes } from '@configs/routes.json';

import { RequestStatus, ICreateUser } from '@store/types';

import Welcome, { IWelcomeStateProps } from './Welcome';
import AccountCreated from './AccountCreated';

const emailRegex = /\S+@\S+\.\S+/;

export interface CreateAccountStateProps extends IWelcomeStateProps {
  error?: JSX.Element;
  status: RequestStatus;
}

interface CreateAccountDispatchProps {
  createUser: (details: ICreateUser) => void;
}

const CreateUser: React.FC<
  CreateAccountStateProps & CreateAccountDispatchProps
> = ({
  createUser: createAccount,
  error,
  status,
  message: marketingMessage,
}) => {
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [institution, setInstitution] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [reenterEmail, setReenterEmail] = React.useState('');
  const { t } = useTranslation();

  const [validationErrors, setValidationErrors] = React.useState<JSX.Element[]>(
    []
  );

  const onCreateAccount = (e: React.MouseEvent) => {
    e.preventDefault();

    const validationErrors: JSX.Element[] = [];

    const payload: ICreateUser = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      institution: institution.trim(),
      email: email.trim(),
    };

    setFirstName(payload.firstName);
    setLastName(payload.lastName);
    setInstitution(payload.institution);
    setEmail(payload.email);

    if (!payload.firstName) {
      validationErrors.push(t('createUser_error_missingFirstName'));
    } else if (
      payload.firstName.length > config.createUser.firstName.maxCharacters
    ) {
      validationErrors.push(
        t('createUser_error_firstNameTooManyCharacters', {
          max: config.createUser.firstName.maxCharacters,
        })
      );
    }
    if (!payload.lastName) {
      validationErrors.push(t('createUser_error_missingLastName'));
    } else if (
      payload.lastName.length > config.createUser.lastName.maxCharacters
    ) {
      validationErrors.push(
        t('createUser_error_lastNameTooManyCharacters', {
          max: config.createUser.lastName.maxCharacters,
        })
      );
    }
    if (!payload.institution) {
      validationErrors.push(t('createUser_error_missingInstitution'));
    } else if (
      payload.institution.length > config.createUser.institution.maxCharacters
    ) {
      validationErrors.push(
        t('createUser_error_institutionTooManyCharacters', {
          max: config.createUser.institution.maxCharacters,
        })
      );
    }
    if (!payload.email) {
      validationErrors.push(t('createUser_error_missingEmail'));
    } else if (payload.email !== reenterEmail) {
      validationErrors.push(t('createUser_error_mismatchedEmail'));
    } else if (!payload.email.match(emailRegex)) {
      validationErrors.push(t('createUser_error_invalidEmail'));
    } else if (payload.email.length > config.createUser.email.maxCharacters) {
      validationErrors.push(
        t('createUser_error_emailTooManyCharacters', {
          max: config.createUser.email.maxCharacters,
        })
      );
    }

    setValidationErrors(validationErrors);

    if (!validationErrors.length) {
      createAccount(payload);
    }
  };

  const allErrors = validationErrors.slice();
  if (error) {
    allErrors.push(error);
  }

  return (
    <div className='create-account login'>
      <Helmet>
        <title>{t('createUser_title')}</title>
      </Helmet>
      <h1>{t('title')}</h1>
      {status === RequestStatus.completed ? (
        <AccountCreated />
      ) : (
        <div>
          <div className='row flexible-height-row'>
            <div className='col-6 p-0'>
              <div className='sub-panel mb-2 p-3'>
                <div className='heading'>
                  {t('createUser_alreadyHaveAnAccount')}
                </div>
                <div>
                  <NavLink
                    to={routes.login}
                    className='font-weight-normal fcolor--secondary-btn-hover'
                  >
                    {t('login_signIn')} <i className='fas fa-chevron-right'></i>
                  </NavLink>
                </div>
              </div>
              <Welcome message={marketingMessage} />
            </div>
            <div className='col-6 pr-0'>
              <div className='login-forms'>
                <div className='credentials-form p-3'>
                  <h2>{t('createUser_createAccount')}</h2>
                  {allErrors.map((error, index) => (
                    <div className='error mb-2' key={index}>
                      {error}
                    </div>
                  ))}
                  <form>
                    <div className='form-group required'>
                      <label htmlFor='firstNameInput'>
                        {t('createUser_firstName')}
                      </label>
                      <input
                        type='text'
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className='form-control'
                        id='firstNameInput'
                      />
                    </div>
                    <div className='form-group required'>
                      <label htmlFor='lastNameInput'>
                        {t('createUser_lastName')}
                      </label>
                      <input
                        type='text'
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className='form-control'
                        id='lastNameInput'
                      />
                    </div>
                    <div className='form-group required'>
                      <label htmlFor='institutionInput'>
                        {t('createUser_institution')}
                      </label>
                      <input
                        type='text'
                        value={institution}
                        onChange={(e) => setInstitution(e.target.value)}
                        className='form-control'
                        id='institutionInput'
                      />
                    </div>
                    <div className='form-group required'>
                      <label htmlFor='emailInput'>
                        {t('createUser_email')}
                      </label>
                      <input
                        type='email'
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className='form-control'
                        id='emailInput'
                      />
                    </div>
                    <div className='form-group required'>
                      <label htmlFor='reenterEmailInput'>
                        {t('createUser_reEnterEmail')}
                      </label>
                      <input
                        type='email'
                        value={reenterEmail}
                        onChange={(e) => setReenterEmail(e.target.value)}
                        className='form-control'
                        id='reenterEmailInput'
                      />
                    </div>
                    <div className='actions text-right'>
                      {status === RequestStatus.inProgress ? (
                        <button className='btn btn-primary btn-sm' disabled>
                          <span
                            className='spinner-border spinner-border-sm mr-2'
                            role='status'
                            aria-hidden='true'
                          ></span>
                          {t('createUser_createAccount', { uppercase: true })}
                        </button>
                      ) : (
                        <button
                          className='btn btn-primary btn-sm'
                          onClick={onCreateAccount}
                        >
                          {t('createUser_createAccount', { uppercase: true })}
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateUser;
